'use client';

import React, { useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { Checkbox, CircularProgress } from 'src/components';
import { useGetRegionAnalyticsQuery } from 'src/api/redux';
import { useAnalytics } from 'src/hooks/analytics';

const AnalyticsStatePanel: React.FC = () => {
  const {
    geographySearch,
    tempSelectedGeographies,
    toggleTempGeography,
    toggleAllTempGeographies,
  } = useAnalytics() || {};

  const [debouncedSearchQuery] = useDebounce(geographySearch, 300);

  // Fetch all regions (shared cache with AnalyticsRegionPanel)
  const { data: allRegions, isFetching } = useGetRegionAnalyticsQuery({
    queryParams: {
      sort: 'name',
      order: 'asc',
    },
  });

  // Filter to only state/country types and apply search
  const states = useMemo(() => {
    if (!allRegions) return [];

    const stateItems = allRegions.filter((r) => r.type === 'state');

    const query = debouncedSearchQuery?.toLowerCase().trim() || '';
    if (!query) return stateItems;

    return stateItems.filter((s) => s.name.toLowerCase().includes(query));
  }, [allRegions, debouncedSearchQuery]);

  // Handle select all
  const handleSelectAll = () => {
    const allStateObjects =
      states?.map((state) => ({
        id: state.id,
        name: state.name,
        type: 'region' as const,
      })) || [];

    toggleAllTempGeographies?.(allStateObjects, 'region');
  };

  const allStatesSelected =
    states &&
    states.length > 0 &&
    states.every((state) => tempSelectedGeographies?.includes(state.id));

  return (
    <div className="flex-1 overflow-y-auto" data-testid="analytics-state-panel">
      {states && states.length > 0 && (
        <div className="w-full px-6 inline-flex flex-col justify-start items-start gap-4 py-4">
          {/* Select All Checkbox */}
          <div
            onClick={handleSelectAll}
            className="inline-flex justify-start items-center gap-2 cursor-pointer"
            data-testid="analytics-state-select-all"
          >
            <Checkbox
              size="sm"
              checked={allStatesSelected || false}
              onChange={handleSelectAll}
              aria-label="Select all states"
            />
            <div className="justify-start text-secondary text-sm font-normal font-['Inter'] leading-5">
              Select All
            </div>
          </div>

          <div className="self-stretch h-0 border-b border-neutral-300" />

          {/* Individual states */}
          {states.map((state: MedScout.Region) => (
            <div
              key={state.id}
              onClick={() =>
                toggleTempGeography?.(state.id, state.name, 'region')
              }
              className="inline-flex justify-start items-center gap-2 cursor-pointer"
              data-testid={`analytics-state-${state.id}`}
            >
              <Checkbox
                size="sm"
                checked={tempSelectedGeographies?.includes(state.id) || false}
                onChange={() =>
                  toggleTempGeography?.(state.id, state.name, 'region')
                }
                aria-label={`Select ${state.name}`}
              />
              <div className="justify-start text-secondary text-sm font-['Inter'] leading-5">
                {state.name}
              </div>
            </div>
          ))}
        </div>
      )}
      {isFetching && (
        <div className="h-full w-full flex items-center justify-center px-4 py-2 text-center text-xs text-neutral-500">
          <CircularProgress size="sm" color="neutral" />
        </div>
      )}
      {!isFetching && states.length === 0 && (
        <div className="h-full w-full flex items-center justify-center px-4 py-2 text-center text-xs text-neutral-500">
          No states found
        </div>
      )}
    </div>
  );
};

export default AnalyticsStatePanel;
